// TUS Protocol Upload Service
import * as tus from 'tus-js-client';
import { loggerUtils } from './logger';
import { config } from '../config';

const API_BASE_URL = config.api.baseUrl;

export interface TusUploadOptions {
  file: File;
  bucketId: number;
  onProgress?: (bytesUploaded: number, bytesTotal: number) => void;
  onSuccess?: (fileId: number) => void;
  onError?: (error: Error) => void;
  abortSignal?: AbortSignal;
  metadata?: Record<string, string>;
  chunkSize?: number;
}

export interface TusUploadSession {
  upload: tus.Upload;
  url?: string;
  bytesUploaded: number;
  bytesTotal: number;
  isPaused: boolean;
}

export class TusUploadService {
  private activeSessions: Map<string, TusUploadSession> = new Map();

  /**
   * Start a TUS upload
   */
  async startUpload(options: TusUploadOptions): Promise<string> {
    const {
      file,
      bucketId,
      onProgress,
      onSuccess,
      onError,
      abortSignal,
      metadata = {},
      chunkSize = 5 * 1024 * 1024, // 5MB default chunk size
    } = options;

    const sessionId = crypto.randomUUID();

    // Track file_id from final PATCH response
    let capturedFileId: number | null = null;

    // Prepare metadata for TUS
    const tusMetadata = {
      filename: file.name,
      filetype: file.type || 'application/octet-stream',
      bucket_id: String(bucketId),
      ...metadata,
    };

    // Create TUS upload instance
    const upload = new tus.Upload(file, {
      // TUS endpoint - will be implemented in backend
      endpoint: `${API_BASE_URL}/v1/files/tus/`,

      // Retry configuration
      retryDelays: [0, 1000, 3000, 5000],

      // Metadata
      metadata: tusMetadata,

      // Chunk size
      chunkSize,

      // Enable resumable uploads
      removeFingerprintOnSuccess: true,

      // Store upload URL for resumption (uses localStorage by default)
      storeFingerprintForResuming: true,

      // Enable parallel chunk uploads for better performance
      parallelUploads: 1, // TUS spec requires sequential chunks

      // Callbacks
      onError: (error) => {
        console.error(`❌ Upload failed: ${file.name}`, error.message);
        this.activeSessions.delete(sessionId);
        onError?.(error);
      },

      onProgress: (bytesUploaded, bytesTotal) => {
        const session = this.activeSessions.get(sessionId);
        if (session) {
          session.bytesUploaded = bytesUploaded;
          session.bytesTotal = bytesTotal;
        }

        onProgress?.(bytesUploaded, bytesTotal);
      },

      onSuccess: () => {
        // Use the file_id captured from the final PATCH response
        if (capturedFileId && capturedFileId > 0) {
          onSuccess?.(capturedFileId);
        } else {
          // Fallback: try HEAD request if file_id wasn't captured
          this.getFileIdFromSession(upload.url || '')
            .then((fileId) => {
              if (fileId && fileId > 0) {
                onSuccess?.(fileId);
              } else {
                console.warn('Upload completed but file_id could not be retrieved');
                onSuccess?.(0);
              }
            })
            .catch((err) => {
              console.error('Failed to get file_id:', err);
              onSuccess?.(0);
            });
        }

        this.activeSessions.delete(sessionId);
      },

      onAfterResponse: (req, res) => {
        // Capture file_id from final PATCH response (when upload completes)
        const method = req.getMethod();
        const status = res.getStatus();

        // Check if this is the final PATCH response (status 204 with metadata)
        if (method === 'PATCH' && status === 204) {
          const metadata = res.getHeader('upload-metadata');
          if (metadata) {
            // Parse file_id from metadata header
            const fileIdMatch = metadata.match(/file_id\s+([A-Za-z0-9+/=]+)/);
            if (fileIdMatch && fileIdMatch[1]) {
              try {
                const decoded = atob(fileIdMatch[1]);
                const fileId = parseInt(decoded, 10);
                if (!isNaN(fileId) && fileId > 0) {
                  capturedFileId = fileId;
                }
              } catch (err) {
                console.error('Failed to parse file_id from metadata:', err);
              }
            }
          }
        }
      },
    });

    // Store session
    const session: TusUploadSession = {
      upload,
      bytesUploaded: 0,
      bytesTotal: file.size,
      isPaused: false,
    };

    this.activeSessions.set(sessionId, session);

    // Handle abort signal
    if (abortSignal) {
      abortSignal.addEventListener('abort', () => {
        this.abortUpload(sessionId);
      });
    }

    // Find previous upload to resume
    const previousUploads = await upload.findPreviousUploads();
    if (previousUploads.length > 0) {
      loggerUtils.upload('resume', file.name, undefined, {
        sessionId,
        previousUploadUrl: previousUploads[0].uploadUrl,
      });
      upload.resumeFromPreviousUpload(previousUploads[0]);
    }

    // Start the upload
    upload.start();

    return sessionId;
  }

  /**
   * Pause an active upload
   */
  pauseUpload(sessionId: string): boolean {
    const session = this.activeSessions.get(sessionId);
    if (!session) return false;

    session.upload.abort();
    session.isPaused = true;

    return true;
  }

  /**
   * Resume a paused upload
   */
  resumeUpload(sessionId: string): boolean {
    const session = this.activeSessions.get(sessionId);
    if (!session || !session.isPaused) return false;

    session.upload.start();
    session.isPaused = false;

    return true;
  }

  /**
   * Abort an upload and clean up
   */
  abortUpload(sessionId: string): boolean {
    const session = this.activeSessions.get(sessionId);
    if (!session) return false;

    session.upload.abort(true); // true = shouldTerminate (delete from server)
    this.activeSessions.delete(sessionId);

    return true;
  }

  /**
   * Get upload progress
   */
  getProgress(sessionId: string): { bytesUploaded: number; bytesTotal: number } | null {
    const session = this.activeSessions.get(sessionId);
    if (!session) return null;

    return {
      bytesUploaded: session.bytesUploaded,
      bytesTotal: session.bytesTotal,
    };
  }

  /**
   * Get file ID from TUS upload session by making a HEAD request
   */
  private async getFileIdFromSession(uploadUrl: string): Promise<number | null> {
    if (!uploadUrl) return null;

    try {
      // Make a HEAD request to get upload status
      // Backend should include file_id in response headers when upload is complete
      const response = await fetch(uploadUrl, {
        method: 'HEAD',
        credentials: 'include',
      });

      if (response.ok) {
        // Try to get file_id from Upload-Metadata header or custom header
        const metadata = response.headers.get('Upload-Metadata');
        if (metadata) {
          // Parse metadata for file_id
          const fileIdMatch = metadata.match(/file_id\s+([A-Za-z0-9+/=]+)/);
          if (fileIdMatch && fileIdMatch[1]) {
            const decoded = atob(fileIdMatch[1]);
            const fileId = parseInt(decoded, 10);
            if (!isNaN(fileId)) return fileId;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error fetching file_id from TUS session:', error);
      return null;
    }
  }

  /**
   * Get all active sessions
   */
  getActiveSessions(): string[] {
    return Array.from(this.activeSessions.keys());
  }

  /**
   * Check if TUS is supported by the browser
   */
  static isSupported(): boolean {
    return tus.isSupported;
  }
}

// Singleton instance
export const tusUploadService = new TusUploadService();
